import {createReminderNamespace} from '../craft/reminders.js';
const SOURCE='dymistrz-planner';
const ROUTE='#/smokings';
const ID_BASE=220_000_000;
const ID_RANGE=9_000_000;

const namespace=createReminderNamespace({source:SOURCE,min:ID_BASE,size:ID_RANGE,keyLabel:'reminder key'});
export const plannerReminderId=namespace.id;
export const isDymistrzPlannerNotification=namespace.owns;

function nowMillis(now){
  const raw=typeof now==='function'?now():now;
  if(raw instanceof Date)return raw.getTime();
  if(typeof raw==='number'&&Number.isFinite(raw))return raw;
  const millis=Date.parse(String(raw||''));
  if(Number.isNaN(millis))throw new TypeError('now must be a valid timestamp');
  return millis;
}

function reminder({id,kind,at,body}){
  return Object.freeze({
    id,
    title:'Dymistrz',
    body,
    schedule:Object.freeze({
      at:new Date(at),
      allowWhileIdle:true,
      isExactNotification:false
    }),
    extra:Object.freeze({
      source:SOURCE,
      route:ROUTE,
      kind
    })
  });
}

function startMillis(block){
  const millis=Date.parse(String(block?.applied?.startAt||block?.startAt||''));
  return Number.isNaN(millis)?null:millis;
}

function blockKind(block){
  return String(block?.kind||'').trim().toLowerCase();
}

function stageType(block){
  return String(block?.stageType||'').trim().toLowerCase();
}

function enabledForBlock(block,preferences){
  const kind=blockKind(block);
  if(kind==='preparation')return preferences.preparationEnabled!==false;
  if(kind==='curing')return preferences.curingEnabled!==false;
  if(kind==='drying')return preferences.dryingEnabled!==false;
  return false;
}

function preparationBody(block){
  const kind=blockKind(block);
  if(kind==='curing')return 'Czas rozpocząć zaplanowane peklowanie lub marynowanie.';
  if(kind==='drying')return 'Czas rozpocząć zaplanowane osuszanie.';
  return 'Czas rozpocząć zaplanowane przygotowanie produktu.';
}

export function createPlannerReminderPlan({
  session,
  preferences,
  now=Date.now
}={}){
  if(!session?.id)throw new TypeError('session is required');
  const snapshot=session.plannerScheduleSnapshot;
  if(!snapshot||snapshot.snapshotVersion!==1||!Array.isArray(snapshot.blocks)){
    return Object.freeze([]);
  }
  const prefs=preferences||{};
  if(prefs.enabled!==true)return Object.freeze([]);

  const current=nowMillis(now);
  const blocks=snapshot.blocks;
  const desired=[];

  for(const block of blocks){
    if(!enabledForBlock(block,prefs))continue;
    const at=startMillis(block);
    if(at==null||at<=current)continue;
    desired.push(reminder({
      id:plannerReminderId('block:'+session.id+':'+block.id+':'+blockKind(block)),
      kind:blockKind(block)+'_start',
      at,
      body:preparationBody(block)
    }));
  }

  const process=blocks
    .filter(block=>String(block?.lane||'')==='process')
    .map(block=>({block,at:startMillis(block)}))
    .filter(item=>item.at!=null)
    .sort((a,b)=>a.at-b.at);
  const processStart=process[0]?.at??null;

  if(processStart!=null&&prefs.prepareSmokerEnabled!==false){
    const lead=Math.max(0,Math.min(240,Number(prefs.prepareSmokerLeadMinutes)||0));
    const at=processStart-lead*60_000;
    if(at>current){
      desired.push(reminder({
        id:plannerReminderId('prepare-smoker:'+session.id),
        kind:'prepare_smoker',
        at,
        body:lead>0
          ?'Przygotuj wędzarnię — początek procesu zbliża się.'
          :'Czas przygotować wędzarnię do rozpoczęcia procesu.'
      }));
    }
  }

  const liveCandidate=process.find(item=>
    ['smoking','warming','drying','baking','steaming','custom'].includes(stageType(item.block))
  )||process[0]||null;
  if(liveCandidate&&prefs.liveStartEnabled!==false){
    const lead=Math.max(0,Math.min(120,Number(prefs.liveStartLeadMinutes)||0));
    const at=liveCandidate.at-lead*60_000;
    if(at>current){
      desired.push(reminder({
        id:plannerReminderId('live-start:'+session.id),
        kind:'live_start',
        at,
        body:lead>0
          ?'Zaplanowany proces wędzenia wkrótce się rozpocznie.'
          :'Czas rozpocząć zaplanowany proces wędzenia.'
      }));
    }
  }

  const byId=new Map();
  for(const item of desired){
    if(byId.has(item.id))throw new Error('PLANNER_REMINDER_ID_COLLISION');
    byId.set(item.id,item);
  }
  return Object.freeze([...byId.values()].sort((a,b)=>
    a.schedule.at-b.schedule.at||a.id-b.id
  ));
}

export const PLANNER_REMINDER_ID_RANGE=Object.freeze({
  min:ID_BASE,
  maxExclusive:ID_BASE+ID_RANGE
});
